import { minutesToMilliseconds } from 'date-fns/minutesToMilliseconds'
import { useQueryShoppingStores } from '@/features/shop/services/ShoppingStore/queries'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { useUserId, useZipForZipOnlySignedInUser } from '@/features/account/services/User/hooks'
import { useGuestParams } from '@/features/account/services/GuestUser/hooks'
import { fetchAllShoppingCarts } from '@/features/shop/services/Carts/fetchers'
import { type AllShoppingCartsInput } from '@/features/shop/services/Carts/types'
import { useDefaultShoppingAddress } from '@/features/account/services/Addresses/hooks'

export const AllCartsPrimaryKey = 'AllShoppingCarts'

const getAllShoppingCartsQueryKey = (params: AllShoppingCartsInput) => {
  const { userId, address, shoppingStoresResponse } = params
  const { street1, city, zip_code, state } = address ?? {}
  const stores = (shoppingStoresResponse?.delivery_stores ?? []).reduce<
    {
      metro_id: number
      store_id: number
      store_location_id: number
    }[]
  >(
    (acc, { id, metro_id, store_location_id }) => [
      ...acc,
      { store_id: id, metro_id, store_location_id },
    ],
    [],
  )
  return [
    AllCartsPrimaryKey,
    {
      address: { addressLineOne: street1, city, zip: zip_code, state },
      stores,
      userId,
    },
  ] as const
}
export type AllShoppingCartsQueryKey = ReturnType<typeof getAllShoppingCartsQueryKey>

const shoppingCartsQueryOptions = (params: AllShoppingCartsInput) =>
  queryOptions({
    queryKey: getAllShoppingCartsQueryKey(params),
    queryFn: fetchAllShoppingCarts,
    staleTime: minutesToMilliseconds(15),
  })

export const useQueryAllShoppingCarts = () => {
  const userId = useUserId() ?? 0
  const { data: shoppingStoresResponse } = useQueryShoppingStores()
  const userAddress = useDefaultShoppingAddress()
  const { guestAddress } = useGuestParams() ?? {}
  const address = userId ? userAddress : guestAddress
  const C360ProvidedZip = useZipForZipOnlySignedInUser()
  return useQuery({
    ...shoppingCartsQueryOptions({ userId, address, shoppingStoresResponse }),
    enabled: Boolean(shoppingStoresResponse && (address?.street1 || C360ProvidedZip)),
  })
}
